import { useEffect, useState } from 'react';
import { Box, CircularProgress, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { DialogWrapper } from '../display-elements-components/dialog-wrapper';
import CapitalGoodService from '../../../shared/services/capital-good/capital-good-service';
import { IMaterialFactor } from '../../../shared/services/capital-good/capital-good.model';

interface DefraFactorDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (factor: IMaterialFactor) => void;
}

export const DefraFactorDialog: React.FC<DefraFactorDialogProps> = ({ open, onClose, onSelect }) => {
  const capitalGoodService = new CapitalGoodService();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<IMaterialFactor[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<IMaterialFactor | null>(null);

  useEffect(() => {
    if (open) { setSearch(''); setSelected(null); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const handle = setTimeout(() => {
      capitalGoodService.searchMaterialFactors(search)
        .then((rows) => { if (active) setResults(rows); })
        .catch(() => { if (active) setResults([]); })
        .finally(() => { if (active) setLoading(false); });
    }, 300);
    return () => { active = false; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, open]);

  const handleSubmit = () => {
    if (selected) { onSelect(selected); onClose(); }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title="Choose a DEFRA material factor"
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={false}
      disableSaveButton={!selected}
      maxWidth="sm"
    >
      <TextField
        fullWidth
        size="small"
        autoFocus
        placeholder="Search materials (e.g. steel, cardboard, plastic)…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
      />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
      ) : results.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No matching DEFRA factors.</Typography>
      ) : (
        <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {results.map((f) => (
            <ListItemButton key={f.id} selected={selected?.id === f.id} onClick={() => setSelected(f)}>
              <ListItemText
                primary={f.label}
                secondary={`${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 3 }).format(f.multiplier)} kg CO₂e per ${f.unitName || 'unit'}${f.calendarYear ? ` · ${f.calendarYear}` : ''}`}
              />
            </ListItemButton>
          ))}
        </List>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Selecting a factor sets the product's emission factor and unit (rated Medium data quality).
      </Typography>
    </DialogWrapper>
  );
};
